import * as React from 'react';

import { useEffect, useCallback, useMemo, useRef } from 'react';
import { onPageDidMount, onPageWillUnmount } from '../../redux/core';
import { useDispatch } from 'react-redux';
import { fetchWebhookConfig, fetchWebhookLog, fetchWebhookLogs, replayWebhook } from '../../api/webhooks';
import { Config, Log, LogWithData } from '../../api/webhooks.types';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DateSelector } from '../../components/core/DateSelector';
import {
  Breadcrumb,
  CheckboxVisibility,
  ConstrainMode,
  DefaultButton,
  DetailsList,
  Label,
  MessageBar,
  MessageBarType,
  Panel,
  PanelType,
  Selection,
  SelectionMode,
  Spinner,
  SpinnerSize,
} from '@fluentui/react';
import moment from 'moment';
import { useQueryString } from '../../util/hooks';
import { DocumentTitle } from '../../components/core/DocumentTitle';
import { ROUTES } from '../../config/routes';

export const WebhookSubscriber = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [date, setDate] = useQueryString('date', moment().format('YYYY-MM-DD'));
  const [fetching, setFetching] = useState(false);
  const [config, setConfig] = useState<Config>();
  const [logs, setLogs] = useState<Log[]>([]);
  const { subscriberId } = useParams<{ subscriberId: string }>();
  const [selectedLogId, setSelectedLogId] = useState<string>();
  const [selectedLog, setSelectedLog] = useState<LogWithData>();
  const [selectedLogFetching, setSelectedLogFetching] = useState(false);

  useEffect(() => {
    fetchWebhookConfig(subscriberId!).then(setConfig);
    dispatch(onPageDidMount('WebhookSubscriber'));
    return () => {
      dispatch(onPageWillUnmount('WebhookSubscriber'));
    };
  }, [dispatch, subscriberId]);

  useEffect(() => {
    setFetching(true);
    setSelectedLog(undefined);
    setSelectedLogFetching(false);
    fetchWebhookLogs(subscriberId!, date).then((data) => {
      setLogs(data || []);
      setFetching(false);
    });
  }, [subscriberId, date]);

  useEffect(() => {
    if (selectedLogId) {
      setSelectedLogFetching(true);
      fetchWebhookLog(subscriberId!, selectedLogId).then((data) => {
        setSelectedLog(data);
        setSelectedLogFetching(false);
      });
    } else {
      setSelectedLog(undefined);
      setSelectedLogFetching(false);
    }
  }, [subscriberId, selectedLogId]);

  const onSelectRow = useCallback((item: Log) => {
    setSelectedLogId(item.id);
  }, []);

  const renderLogData = useCallback(() => {
    const onReplayClick = () => {
      if (selectedLogId) {
        replayWebhook(subscriberId!, selectedLogId);
      }
    };
    return (
      <Panel isBlocking={false} isOpen={!!selectedLogId} onDismiss={() => setSelectedLogId(undefined)} headerText={selectedLogId} type={PanelType.medium}>
        {selectedLogFetching && <Spinner size={SpinnerSize.large} label="Fetching Log details..." />}
        {!selectedLogFetching && selectedLog && (
          <>
            <DefaultButton iconProps={{ iconName: 'PlaybackRate1x' }} text="Replay" onClick={onReplayClick} />
            <Label>Data</Label>
            <pre style={{ padding: 6, background: '#eaeaea' }}>{JSON.stringify(tryParse(selectedLog.data), undefined, 2)}</pre>
            <Label>Response ({selectedLog.statusCode})</Label>
            <pre style={{ padding: 6, background: '#eaeaea' }}>{JSON.stringify(selectedLog.response, undefined, 2)}</pre>
          </>
        )}
      </Panel>
    );
  }, [selectedLog, selectedLogFetching, selectedLogId, subscriberId]);

  return (
    <>
      <DocumentTitle title={`${config?.subscriberName} webhooks`} />

      <Breadcrumb
        items={[
          { text: 'Webhooks', key: 'Webhooks', onClick: () => navigate(ROUTES.webhooks) },
          { text: `${config?.subscriberName || '...'} webhooks`, key: 'WebhookSubscriber' },
        ]}
      />

      <div className="flex flex-center" style={{ marginTop: 10 }}>
        <DateSelector outputFormat="YYYY-MM-DD" defaultDate={moment(date).toDate()} showNudgeButtons={true} onChange={(d) => setDate(d)} />
      </div>
      {fetching && <Spinner size={SpinnerSize.large} />}
      {!fetching && <LogList items={logs} onSelectRow={onSelectRow} />}
      {!fetching && logs.length === 0 && <MessageBar messageBarType={MessageBarType.info}>There are no logs on this date.</MessageBar>}
      {!!selectedLogId && renderLogData()}
    </>
  );
};

const LogList = ({ items, onSelectRow }: { items: Log[]; onSelectRow: (item: Log) => void }) => {
  const columns = useMemo(
    () => [
      { key: 'id', name: 'ID', fieldName: 'id', minWidth: 260, maxWidth: 260 },
      {
        key: 'timestamp',
        name: 'Timestamp',
        fieldName: 'timestamp',
        minWidth: 160,
        maxWidth: 200,
        onRender: (item: Log) => moment.utc(item.timestamp).tz(moment.tz.guess()).format('MM/DD/YYYY h:mm:ss a'),
      },
      {
        key: 'webhookSubscriberId',
        name: 'Webhook Subscriber ID',
        fieldName: 'webhookSubscriberId',
        minWidth: 260,
        maxWidth: 260,
      },
      {
        key: 'conciergeAgencyId',
        name: 'Concierge Agency ID',
        fieldName: 'conciergeAgencyId',
        minWidth: 260,
        maxWidth: 260,
      },
      { key: 'type', name: 'Type', fieldName: 'type', minWidth: 60, maxWidth: 60 },
      {
        key: 'version',
        name: 'Version',
        fieldName: 'version',
        minWidth: 60,
        maxWidth: 60,
      },
      { key: 'url', name: 'Url', fieldName: 'url', minWidth: 260, maxWidth: 260, isResizable: true },
      {
        key: 'statusCode',
        name: 'Status Code',
        fieldName: 'statusCode',
        minWidth: 80,
        maxWidth: 120,
      },
      {
        key: 'response',
        name: 'Response',
        fieldName: 'response',
        minWidth: 80,
        maxWidth: 120,
      },
    ],
    []
  );
  const selection = useRef(
    new Selection({
      getKey: (data: any) => data.id,
      canSelectItem: () => true,
      onSelectionChanged: () => {
        const selected = selection.current.getSelection();
        if (selected && selected[0]) {
          onSelectRow(selected[0] as Log);
        }
      },
    })
  );

  useEffect(() => {
    selection.current.setItems(items);
  }, [items]);

  return (
    <DetailsList
      compact={true}
      selectionMode={SelectionMode.single}
      selection={selection.current}
      constrainMode={ConstrainMode.horizontalConstrained}
      checkboxVisibility={CheckboxVisibility.hidden}
      items={items}
      setKey="set"
      columns={columns}
    />
  );
};

function tryParse(str: string) {
  let result = str;
  try {
    result = JSON.parse(str);
  } finally {
    // do nothing
  }
  return result;
}
